import { FormProvider } from 'react-hook-form';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import gettext from '@sdk/lang/gettext';
import { DismissButton } from '@sdk/components/ui/InfoModal/infoInnerModal.styles';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { FormLayout } from '@sdk/components/ui/modalForm/modalForm.styles';

import ShiftTaskIcon from '@web/assets/svg/Icons/32/shift-blue.svg';
import BodyPortal from '@web/components/BodyPortal';
import { TitleField } from '@web/shiftTasks/ComposeTask/TitleField/TitleField';
import { DescriptionField } from '@web/shiftTasks/ComposeTask/DescriptionField/DescriptionField';
import { TaskEditModal } from '@web/shiftTasks/ComposeTask/composeTask.styles';
import { TaskTemplate } from '@web/shiftTasks/types/TaskTemplate';
import { AttachmentsField } from '@web/shiftTasks/ComposeTask/AttachmentsField/AttachmentsField';
import { useShiftTaskAttachmentsFeatureFlag } from '@web/shiftTasks/hooks/useShiftTaskAttachmentsFeatureFlag';
import { SubtasksField } from '@web/shiftTasks/ComposeTask/Subtasks/SubtasksField/SubtasksField';

import { TemplateEditingOptionsModal } from './TemplateEditingOptionsModal/TemplateEditingOptionsModal';
import { useEditTaskTemplateForm } from './editTaskTemplate.hooks';

type Props = {
  modalState: ModalState;
  originalTemplate: TaskTemplate;
};

export const EditTaskTemplate = ({ modalState, originalTemplate }: Props) => {
  const { areAttachmentsEnabled } = useShiftTaskAttachmentsFeatureFlag();
  const { onSubmit, formMethods, saveTaskTemplate, taskEditingOptionsModalState, title } =
    useEditTaskTemplateForm(modalState, originalTemplate);

  const {
    formState: { isSubmitting, isValid, isDirty },
  } = formMethods;
  const isSubmitActive = isDirty && isValid;

  return (
    <BodyPortal>
      <TaskEditModal
        titleText={title}
        titleIcon={<ShiftTaskIcon />}
        dismiss={modalState.close}
        buttons={
          <>
            <DismissButton onClick={modalState.close}>{gettext('Cancel')}</DismissButton>
            <DismissButton
              buttonType={isValid ? ButtonType.primary : ButtonType.disabledStyle}
              onClick={onSubmit}
              inactive={!isSubmitActive || isSubmitting}>
              {gettext('Save')}
            </DismissButton>
          </>
        }>
        <FormProvider {...formMethods}>
          <form>
            <FormLayout>
              <TitleField />
              <DescriptionField />
              <SubtasksField />
              {areAttachmentsEnabled && <AttachmentsField />}
            </FormLayout>
          </form>
        </FormProvider>
      </TaskEditModal>

      {taskEditingOptionsModalState.isVisible && (
        <TemplateEditingOptionsModal
          onClose={taskEditingOptionsModalState.close}
          onSubmit={saveTaskTemplate}
        />
      )}
    </BodyPortal>
  );
};
